import { FC, ReactNode, useMemo } from 'react';

import classNames from 'classnames';

import Image from '../../Image';
import Link from '../../Link';
import Typography from '../../Typography';
import { useResponsiveNavBar } from '../NavBarContext';
import NavBarItem from '../NavBarItem';

import './Brand.scss';

import { IMAGE_URL } from '../../_utils/config';

import { TestMetadata } from '../../types';

export interface BrandProps {
  /** Pass in src of image URL to override the default logo */
  logoSrc?: string;
  /** Pass in your own logo to use */
  logo?: ReactNode;
  /** Text to display after the logo - T50 variant */
  text?: string;
  /** Flag to determine if you want text to hide on mobile */
  hideTextOnMobile?: boolean;
  /** Path for on click, root default */
  path?: string;
  /** Classname for site brand section */
  className?: string;
  /** TestMetadata for site brand - wrapper, logo, text */
  testMetadata?: {
    wrapper?: TestMetadata;
    link?: TestMetadata;
    logo?: TestMetadata;
    text?: TestMetadata;
  };
}

const Brand: FC<BrandProps> = ({
  logoSrc = `${IMAGE_URL}/logos/DMED_Icon_White@3x.png`,
  logo,
  text,
  hideTextOnMobile = true,
  path = '/',
  className,
  testMetadata,
  ...otherProps
}) => {
  const { getBreakpointClassName } = useResponsiveNavBar();
  const siteBrandClassNames = useMemo(
    () => classNames('sb-brand', className),
    [className],
  );

  return (
    <NavBarItem
      className={siteBrandClassNames}
      {...testMetadata?.wrapper}
      {...otherProps}
    >
      <Link href={path} {...testMetadata?.link} className="sb-brand__link">
        {logo ? (
          logo
        ) : (
          <Image
            className="sb-brand__logo"
            src={logoSrc}
            alt={text ? text : 'App Logo'}
            {...testMetadata?.logo}
          />
        )}

        {text && (
          <Typography
            variant="T50"
            bold
            className={classNames('sb-brand__text', {
              [getBreakpointClassName('sb-brand__hide-on-mobile')]:
                hideTextOnMobile,
            })}
            {...testMetadata?.text}
          >
            {text}
          </Typography>
        )}
      </Link>
    </NavBarItem>
  );
};

export default Brand;
